<template>
    <div>
        <div class="space-y-4">
            <template v-for="(entry, index) in groupedLineItems" :key="index">
                <div
                    v-if="entry.type === 'single'"
                    class="flex items-center justify-between min-h-[2.5rem] py-1"
                    :data-test="`line-item-${index}`"
                >
                    <div class="flex-1 flex flex-col justify-center">
                        <div class="text-sm text-gray-900" data-test="item-name">
                            {{ entry.item.name }}
                        </div>
                        <div v-if="entry.item.quantity && entry.item.unit_amount" class="text-xs text-gray-500 mt-0.5">
                            {{ formatNumber(entry.item.quantity) }}
                            {{ entry.item.quantity === 1 ? 'unit' : 'units' }} @ ${{
                                formatRate(entry.item.unit_amount)
                            }}
                        </div>
                    </div>
                    <div class="text-sm font-medium text-gray-900 ms-4 tabular-nums">
                        {{ currencyCents(entry.item.total_cents) }}
                    </div>
                </div>

                <div v-else :data-test="`line-item-group-${entry.category}`">
                    <button
                        type="button"
                        class="flex items-center justify-between min-h-[2.5rem] py-1 w-full text-start cursor-pointer"
                        :aria-expanded="expandedGroups.has(entry.category)"
                        data-test="group-toggle"
                        @click="toggleGroup(entry.category)"
                    >
                        <div class="flex items-center gap-1.5">
                            <svg
                                class="w-3.5 h-3.5 text-gray-400 transition-transform"
                                :class="{ 'rotate-90': expandedGroups.has(entry.category) }"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                            <span class="text-sm text-gray-900" data-test="item-name">{{ entry.label }}</span>
                        </div>
                        <div class="text-sm font-medium text-gray-900 ms-4 tabular-nums">
                            {{ currencyCents(entry.totalCents) }}
                        </div>
                    </button>

                    <div v-if="expandedGroups.has(entry.category)" class="ps-5">
                        <div
                            v-for="(child, childIndex) in entry.children"
                            :key="childIndex"
                            class="flex items-center justify-between min-h-[2rem] py-2"
                            :data-test="`line-item-child-${childIndex}`"
                        >
                            <div class="flex-1 flex flex-col justify-center">
                                <div class="text-xs text-gray-600" data-test="item-name">
                                    {{ child.name }}
                                </div>
                                <div v-if="child.quantity && child.unit_amount" class="text-xs text-gray-400 mt-0.5">
                                    {{ formatNumber(child.quantity) }}
                                    {{ child.quantity === 1 ? 'unit' : 'units' }} @ ${{ formatRate(child.unit_amount) }}
                                </div>
                            </div>
                            <div class="text-xs font-medium text-gray-600 ms-4 tabular-nums">
                                {{ currencyCents(child.total_cents) }}
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <div v-if="props.showTotal" class="mt-4 pt-4 border-t border-gray-100">
            <div class="flex justify-between items-baseline">
                <span class="text-sm text-gray-600">Estimated total</span>
                <span class="text-lg font-semibold text-gray-900 tabular-nums">
                    {{ currencyCents(props.totalCents) }}
                </span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { currencyCents, formatNumber } from '~/utils/helpers';

const props = defineProps({
    totalCents: {
        type: Number,
        required: true,
    },
    lineItems: {
        type: Array,
        required: true,
    },
    showTotal: {
        type: Boolean,
        default: true,
    },
});

const COLLAPSIBLE_CATEGORIES = ['sms_carrier_fees', 'mms_carrier_fees'];

const groupedLineItems = computed(() => {
    const result = [];
    const groups = {};

    for (const item of props.lineItems) {
        if (COLLAPSIBLE_CATEGORIES.includes(item.category)) {
            if (!groups[item.category]) {
                groups[item.category] = {
                    type: 'group',
                    category: item.category,
                    label: item.display_name,
                    totalCents: 0,
                    children: [],
                };
                result.push(groups[item.category]);
            }
            groups[item.category].totalCents += item.total_cents;
            groups[item.category].children.push(item);
        } else {
            result.push({ type: 'single', item });
        }
    }

    return result.map((entry) => {
        if (entry.type === 'group' && entry.children.length === 1) {
            return { type: 'single', item: entry.children[0] };
        }
        return entry;
    });
});

const expandedGroups = ref(new Set());

const toggleGroup = (category) => {
    const next = new Set(expandedGroups.value);
    if (next.has(category)) {
        next.delete(category);
    } else {
        next.add(category);
    }
    expandedGroups.value = next;
};

const formatRate = (rate) => {
    const formatted = parseFloat(rate.toFixed(5)).toString();
    const parts = formatted.split('.');
    if (!parts[1] || parts[1].length < 2) {
        return rate.toFixed(2);
    }
    return formatted;
};
</script>
